import { NODE_WIDTH } from '@src/consts';
import type { CustomNode } from '@src/components/custom-nodes/types';
import { getNodesBetween, getNearestNodeByType } from '@src/scenario-editor/utils';
// import { getSiblings } from '@src/scenario-editor/utils/get-siblings';
import type { ReactFlowInstance } from 'reactflow';

// INFO: nodes example with only relevant fields for calculation
// const nodes = [
//     { branchId: "1" },
//     { branchId: "1-1", parentBranchId: "1" },
//     { branchId: "1-1", parentBranchId: "1" },
//     { branchId: "1-2", parentBranchId: "1" },
//     { branchId: "1-1-1", parentBranchId: "1-1" },
//     { branchId: "1-1-2", parentBranchId: "1-1" },
//     { branchId: "1-1-3", parentBranchId: "1-1" },
//     { branchId: "1-1-3", parentBranchId: "1-1" },
//     { branchId: "1-1-3", parentBranchId: "1-1" },
//     { branchId: "1-2-1", parentBranchId: "1-2" },
//     { branchId: "1-2-2", parentBranchId: "1-2" },
//     { branchId: "1-2-2", parentBranchId: "1-2" },
// ]

interface BranchNode {
  id?: string;
  branchId: string;
  parentBranchId: string;
  parentNodeId?: string;
  children: BranchNode[];
  x?: number;
}

const NODE_WIDTH_WITH_GAP = NODE_WIDTH + 40;

function getClosingNodes(nodes: string[]) {
  const allNodes = new Set(nodes);
  const nonClosingNodes = new Set();

  nodes.forEach(node => {
    const parts = node.split('-');

    for (let i = 1; i < parts.length; i++) {
      const parent = parts.slice(0, i).join('-');
      nonClosingNodes.add(parent);
    }
  });

  const closingNodes = [...allNodes].filter(node => !nonClosingNodes.has(node));

  return closingNodes;
}

export const getBranchPositions = (nodes: CustomNode[], { instance }: { instance: ReactFlowInstance }, levelWidth?: number) => {
  const nodesWithUniqueBranchIds = nodes.reduce((acc: BranchNode[], node: CustomNode) => {
    const parentNode = node.parentNodeId ? nodes.find(n => n.id === node.parentNodeId) : undefined;
    const currentBranchLength = node.branchId.split('-').length;
    const parentBranchLength = parentNode?.branchId.split('-').length ?? 0;
    if (
      !acc.find(n =>
        (n.branchId === node.branchId && n.parentNodeId === node.parentNodeId)
        || (n.branchId === node.branchId && node.branchId === '1')
      )
      && currentBranchLength === parentBranchLength + 1
    ) {
    // if ( !acc.find(n => n.branchId === node.branchId)) {
      acc.push({ branchId: node.branchId, parentBranchId: node.parentBranchId, parentNodeId: node.parentNodeId, id: node.id } as BranchNode);
    }
    return acc;
  }, []).map(n => ({...n, children: [] }));

  const closingNodes = getClosingNodes(nodesWithUniqueBranchIds.map(n => n.branchId).sort());

  if (levelWidth === undefined) {
    levelWidth = (closingNodes.length) * NODE_WIDTH_WITH_GAP;
  }

  // Build a tree structure
  nodesWithUniqueBranchIds.forEach(node => {
    if (node.parentBranchId) {
      const nodesWithParentBranchId = nodesWithUniqueBranchIds.filter(n => n.branchId === node.parentBranchId);
      if (nodesWithParentBranchId.length === 1) {
        const parent = nodesWithUniqueBranchIds.find(n => n.branchId === node.parentBranchId) as BranchNode;
        parent?.children?.push(node);
        parent.children = (parent?.children || []).sort((a, b) => a.branchId.localeCompare(b.branchId));
      } else {
        const parentNodeIds = nodesWithParentBranchId.map(n => n.parentNodeId);
        let closestParentNode = node.parentNodeId;
        while (closestParentNode && !parentNodeIds.includes(closestParentNode)) {
          const closestParentNodeInstance = nodes.find(n => n.id === closestParentNode);
          closestParentNode = closestParentNodeInstance?.parentNodeId;
        }
        const parentNode = nodesWithParentBranchId.find(n => n.parentNodeId === closestParentNode);
        if (parentNode) {
          const parent = nodesWithUniqueBranchIds.find(n => n.id === parentNode.id) as BranchNode;
          parent?.children?.push(node);
          parent.children = (parent?.children || []).sort((a, b) => a.branchId.localeCompare(b.branchId));
        }
      }
    }
  });

  // Function to assign X positions
  function assignXPositions(node: BranchNode, startX = 0, availableWidth: number, { instance }: { instance: ReactFlowInstance }) {
    const edges = instance.getEdges();
    if (!node.children || node.children.length === 0) {
      node.x = startX; // Leaf node
      return NODE_WIDTH_WITH_GAP; // Return the width used by this node
    } else {
      const childrenGrouped = node.children.reduce((acc: Record<string, BranchNode[]>, child) => {
        acc[child.parentNodeId ?? ''] = acc[child.parentNodeId ?? ''] ?? [];
        acc[child.parentNodeId ?? ''].push(child);
        return acc;
      }, {});
      Object.entries(childrenGrouped).forEach(([, children]) => {
        let currentX = startX;
        children.forEach((child) => {
          let parentIfConditionNodeId = child.parentNodeId;
          let parentIfConditionNode = nodes.find(n => n.id === parentIfConditionNodeId);
          while(parentIfConditionNode?.type !== 'IfConditionNode' && parentIfConditionNode?.type !== 'IfConditionBehaviorNode') {
            parentIfConditionNodeId = parentIfConditionNode?.parentNodeId;
            parentIfConditionNode = nodes.find(n => n.id === parentIfConditionNodeId);
          }
          const closeNode = getNearestNodeByType(nodes, edges, parentIfConditionNode, 'IfCloseNode');
          let childWeight = 1;
          if (closeNode && parentIfConditionNode) {
            const nodesBetween = getNodesBetween(nodes, edges, parentIfConditionNode, closeNode);
            const allLocalNodesToSearchIn = [...nodesBetween, parentIfConditionNode];
            const localClosingNodes = getClosingNodes(allLocalNodesToSearchIn.map(n => n.branchId).sort());
            childWeight = Math.max(
              Array.from(new Set(localClosingNodes.filter(n => n.startsWith(child.branchId)))).length,
              Array.from(new Set(child.children?.map(item => item.branchId))).length ?? 0,
              1
            );
          } else {
            childWeight = Math.max(
              closingNodes.filter(n => n.startsWith(child.branchId)).length,
              Array.from(new Set(child.children?.map(item => item.branchId))).length ?? 0,
              1
            );
          }
          const childWidth = childWeight * NODE_WIDTH_WITH_GAP;
          const usedWidth = assignXPositions(child, currentX, childWidth, { instance });
          currentX += usedWidth;
        });
      });

      // Parent's X is the midpoint of its children
      if (node.children.length > 1) {
        const firstChildX = node.children[0].x ?? 0;
        const lastChildX = node.children[node.children.length - 1].x ?? 0;
        node.x = Math.floor(firstChildX + (lastChildX - firstChildX) / 2);
      } else {
        node.x = node.children[0].x; // If only one child, align with the child
      }

      return availableWidth; // Return the total width used by this subtree
    }
  }
  // Assuming '1' is the root node
  const rootNode = nodesWithUniqueBranchIds.find(n => n.branchId === '1');

  if (rootNode) {
    assignXPositions(rootNode, 0, levelWidth, { instance });
  }
  // Output nodes with their calculated X positions
  const normalizedByBranchId = nodesWithUniqueBranchIds.reduce((acc: Record<string, BranchNode[]>, node) => {
    const foundNodes = nodesWithUniqueBranchIds.filter(n => n.branchId === node.branchId) as BranchNode[];
    acc[node.branchId] = foundNodes;
    return acc;
  }, {});

  return normalizedByBranchId;
}

export const recalculateNodesXPos = (
  nodes: CustomNode[],
  _levelWidth?: number,
  { instance }: { instance?: ReactFlowInstance } = {}
) => {
  // find if some branches collapsed (have the X position difference less than nodeWidth)
  const branchPositionsAdjustedWithMin = getBranchPositions(
    nodes,
    { instance: instance! }
  );

  nodes.forEach(node => {
    const branchNode = branchPositionsAdjustedWithMin[node.branchId];
    let branchNodeIndex = 0;
    if (branchNode.length > 1) {
      const parentNodeIds = branchNode.map(n => n.parentNodeId);
      // find closest parent node from parentNodeIds
      let closestParentNode = node.parentNodeId;
      while (closestParentNode && !parentNodeIds.includes(closestParentNode)) {
        const closestParentNodeInstance = nodes.find(n => n.id === closestParentNode);
        closestParentNode = closestParentNodeInstance?.parentNodeId;
      }
      branchNodeIndex = branchNode.findIndex(n => n.parentNodeId === closestParentNode);
    }
    const x = branchNode[branchNodeIndex].x ?? 0;

    if (instance) {
      // INFO: this part is a basis for further branch recalculation
      // https://dho.atlassian.net/browse/LF360-759

      // const edges = instance?.getEdges() ?? [];
      // const nodeSiblings = getSiblings(node, nodes, edges);
      // const calculatedBranches = branchPositionsAdjustedWithMin[node.parentBranchId ?? '1']?.children;
      // console.log('siblings', nodeSiblings);
    }

    node.position = { ...node.position, x };
  });

  const ifConditionNodes = nodes.filter(node => node.type === 'IfConditionNode' || node.type === 'IfConditionBehaviorNode');
  if (instance) {
    const edges = instance.getEdges();

    ifConditionNodes.forEach(node => {
      const closingNode = getNearestNodeByType(nodes, edges, node, 'IfCloseNode');
      if (closingNode) {
        const nodesBetween = getNodesBetween(nodes, edges, node, closingNode);
        const branchDepth = node.branchId.split('-').length;
        const branchNodes = nodesBetween.filter(n => n.type === 'IfBranchNode' && n.branchId.split('-').length - 1 === branchDepth);
        // get center point of branch nodes
        const centerPoint = branchNodes.reduce((acc, n) => acc + n.position.x, 0) / branchNodes.length;
        const currentNodePositionX = node.position.x;
        const diff = centerPoint - currentNodePositionX;
        nodesBetween.forEach(n => {
          n.position.x -= diff;
        });
      }
    });
  }

  return nodes;
}
